import type { ProcessedFormField } from "@/types/api/formSchema";
import {
  resolveDateConstraints,
  resolveDefaultValue,
} from "@/lib/dateConstraints";
import { isJexlString } from "@/lib/formula";
import { isValidPanFormat } from "@/services/validators";

const PAN_FORMAT_MESSAGE =
  "Enter a valid PAN (10 characters, format ABCDE1234F).";

type PhoneRule = {
  length: number;
  pattern: RegExp;
  message: string;
};

export const PHONE_RULES: Record<string, PhoneRule> = {
  "+91": {
    length: 10,
    pattern: /^[6-9]\d{9}$/,
    message: "Indian numbers must be 10 digits starting with 6, 7, 8, or 9",
  },
  "+65": {
    length: 8,
    pattern: /^[689]\d{7}$/,
    message: "Singapore numbers must be 8 digits starting with 6, 8, or 9",
  },
  "+1": {
    length: 10,
    pattern: /^[2-9]\d{9}$/,
    message: "US numbers must be 10 digits and cannot start with 0 or 1",
  },
  "+44": {
    length: 10,
    pattern: /^[1-9]\d{9}$/,
    message: "UK numbers must be 10 digits (excluding leading 0)",
  },
  "+971": {
    length: 9,
    pattern: /^[2-9]\d{8}$/,
    message: "UAE numbers must be 9 digits starting with 2-9",
  },
  "+61": {
    length: 9,
    pattern: /^[2-9]\d{8}$/,
    message: "Australian numbers must be 9 digits (excluding leading 0)",
  },
  "+49": {
    length: 11,
    pattern: /^[1-9]\d{9,10}$/,
    message: "German numbers must be 10 or 11 digits",
  },
  "+81": {
    length: 10,
    pattern: /^[1-9]\d{8,9}$/,
    message: "Japanese numbers must be 9 or 10 digits",
  },
  "+86": {
    length: 11,
    pattern: /^1[3-9]\d{9}$/,
    message: "Chinese mobile numbers must be 11 digits starting with 1[3-9]",
  },
};

export function validatePhone(
  number: string,
  countryCode: string,
): string | null {
  const digits = number.replace(/\D/g, "");
  const rule = PHONE_RULES[countryCode];

  if (!rule) {
    if (digits.length < 7 || digits.length > 15) {
      return "Phone number must be between 7 and 15 digits";
    }
    return null;
  }

  // Pattern encodes both the character rule AND the acceptable length
  // range (some countries allow a range, e.g. +49 = 10-11, +81 = 9-10).
  // A separate strict-equality length check would falsely reject those.
  if (!rule.pattern.test(digits)) {
    return rule.message;
  }

  return null;
}

export function buildDefaultValues(
  fields: ProcessedFormField[],
  initialValues?: Record<string, unknown>,
): Record<string, unknown> {
  const defaults: Record<string, unknown> = {};
  for (const field of fields) {
    if (initialValues && initialValues[field.name] !== undefined) {
      defaults[field.name] = initialValues[field.name];
      continue;
    }

    if (field.prefilled && field.value !== undefined) {
      defaults[field.name] = field.value;
      continue;
    }

    switch (field.type) {
      case "checkbox":
      case "employee_select":
      case "emails":
        defaults[field.name] = [];
        break;
      case "boolean":
        defaults[field.name] = false;
        break;
      case "phone":
        defaults[field.name] = { number: "", country_code: "+91" };
        break;
      case "attachment":
        defaults[field.name] = null;
        break;
      case "date": {
        const { minDate } = resolveDateConstraints(field.constraints);
        const constraintDefault = resolveDefaultValue(
          field.constraints,
          minDate,
        );
        defaults[field.name] = constraintDefault ?? "";
        break;
      }
      default:
        defaults[field.name] = "";
    }
  }
  return defaults;
}

export function buildFieldRules(
  field: ProcessedFormField,
): Record<string, unknown> {
  const rules: Record<string, unknown> = {};

  if (field.required) {
    const errorMsg = field.errorMsg || `${field.resolvedLabel} is required`;

    if (field.type === "phone") {
      rules["validate"] = (value: {
        number: string;
        country_code: string;
      }) => {
        if (!value?.number?.trim()) return errorMsg;
        return validatePhone(value.number, value.country_code) ?? true;
      };
    } else if (field.type === "checkbox") {
      rules["validate"] = (value: string[]) => {
        if (!value || value.length === 0) return `Select at least one option`;
        return true;
      };
    } else if (field.type === "boolean") {
      rules["validate"] = (value: unknown) => {
        if (value !== true) return errorMsg;
        return true;
      };
    } else if (field.type === "emails") {
      rules["validate"] = (value: string[]) => {
        const nonEmpty = Array.isArray(value)
          ? value.filter((v) => v && v.trim())
          : [];
        if (nonEmpty.length === 0) return errorMsg;
        return true;
      };
    } else if (field.type === "employee_select") {
      rules["validate"] = (value: unknown[]) => {
        if (!value || value.length === 0) return errorMsg;
        return true;
      };
    } else if (field.type === "attachment") {
      rules["validate"] = (
        value: { attachment_id?: string } | null | undefined,
      ) => {
        if (!value || !value.attachment_id) return errorMsg;
        return true;
      };
    } else {
      rules["validate"] = (value: unknown) => {
        if (value === null || value === undefined) return errorMsg;
        const strVal =
          typeof value === "string" ? value.trim() : `${value as number}`;
        if (strVal === "") return errorMsg;
        return true;
      };
    }
  }

  if (field.type === "phone" && !field.required) {
    rules["validate"] = (value: { number: string; country_code: string }) => {
      if (!value?.number?.trim()) return true;
      return validatePhone(value.number, value.country_code) ?? true;
    };
  }

  if (field.type === "email") {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const existingValidate = rules["validate"] as
      | ((value: unknown) => string | true)
      | undefined;

    rules["validate"] = (value: unknown) => {
      if (existingValidate) {
        const result = existingValidate(value);
        if (result !== true) return result;
      }
      const str = String(value).trim();
      if (str === "") return true;
      if (!emailPattern.test(str)) return "Please enter a valid email address";
      return true;
    };
  }

  if (field.type === "emails") {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const existingValidate = rules["validate"] as
      | ((value: unknown) => string | true)
      | undefined;

    rules["validate"] = (value: unknown) => {
      if (existingValidate) {
        const result = existingValidate(value);
        if (result !== true) return result;
      }
      const items = (
        Array.isArray(value)
          ? (value as string[])
          : typeof value === "string" && value
            ? (value as string).split(",")
            : []
      )
        .map((s) => (typeof s === "string" ? s.trim() : ""))
        .filter(Boolean);
      for (const item of items) {
        if (!emailPattern.test(item)) {
          return `"${item}" is not a valid email address`;
        }
      }
      return true;
    };
  }

  if (field.type === "number") {
    const numberPattern = /^-?\d+(\.\d+)?$/;
    const existingValidate = rules["validate"] as
      | ((value: unknown) => string | true)
      | undefined;
    rules["validate"] = (value: unknown) => {
      if (existingValidate) {
        const result = existingValidate(value);
        if (result !== true) return result;
      }
      const str = String(value ?? "").trim();
      if (str === "") return true;
      if (!numberPattern.test(str)) {
        return field.patternMessage || "Enter a valid number (decimals allowed)";
      }
      return true;
    };
  }

  // Date-field constraints — min/max are already enforced by the native
  // `type="date"` input, but disabled_days / disabled_dates can't be. Layer
  // them as a form-level validator so submit is blocked even though the
  // input UI can't visually disable the date.
  if (field.type === "date" && field.constraints) {
    const { disabled_days: disabledDayNums, disabled_dates: disabledDateStrs } =
      field.constraints;
    const disabledDaySet = disabledDayNums?.length
      ? new Set<number>(disabledDayNums)
      : null;
    const disabledDateSet = disabledDateStrs?.length
      ? new Set<string>(disabledDateStrs)
      : null;
    if (disabledDaySet || disabledDateSet) {
      const existingValidate = rules["validate"] as
        | ((value: unknown) => string | true)
        | undefined;
      rules["validate"] = (value: unknown) => {
        if (existingValidate) {
          const r = existingValidate(value);
          if (r !== true) return r;
        }
        if (typeof value !== "string" || !value) return true;
        if (disabledDateSet && disabledDateSet.has(value)) {
          return "This date is not selectable.";
        }
        if (disabledDaySet) {
          // Parse YYYY-MM-DD as UTC so day-of-week is stable across TZs.
          const [y, m, d] = value.split("-").map(Number);
          if (Number.isFinite(y) && Number.isFinite(m) && Number.isFinite(d)) {
            const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
            if (disabledDaySet.has(dow)) {
              return "This day of the week is not selectable.";
            }
          }
        }
        return true;
      };
    }
  }

  // Apply `pattern` for any text-ish input type. `number` gets pattern on top
  // of the default numeric check (pincode is schema type: "number" + a 6-digit
  // pattern); `link` too so vendor-provided URLs can be format-validated
  // beyond the free-form default.
  if (
    field.pattern &&
    (field.type === "text" ||
      field.type === "textarea" ||
      field.type === "email" ||
      field.type === "number" ||
      field.type === "link")
  ) {
    let compiled: RegExp | null = null;
    // Enable the `u` flag when the pattern uses a Unicode property escape
    // (\p{X} / \P{X}) — without it, `\p{L}` compiles as literal escapes and
    // the pattern never matches real alphabetic input. Only opt-in when the
    // marker is present so existing patterns don't shift semantics.
    const needsUnicodeFlag =
      field.pattern.includes("\\p{") || field.pattern.includes("\\P{");
    try {
      compiled = new RegExp(field.pattern, needsUnicodeFlag ? "u" : "");
    } catch {
      compiled = null;
    }
    if (compiled) {
      const existingValidate = rules["validate"] as
        | ((value: unknown) => string | true)
        | undefined;
      rules["validate"] = (value: unknown) => {
        if (existingValidate) {
          const result = existingValidate(value);
          if (result !== true) return result;
        }
        const str = String(value ?? "").trim();
        if (str === "") return true;
        if (!compiled!.test(str)) {
          return (
            field.patternMessage ||
            `${field.resolvedLabel} format is invalid`
          );
        }
        return true;
      };
    }
  }

  if (field.name === "pan_number") {
    const existingValidate = rules["validate"] as
      | ((value: unknown) => string | true)
      | undefined;
    rules["validate"] = (value: unknown) => {
      if (existingValidate) {
        const result = existingValidate(value);
        if (result !== true) return result;
      }
      const str =
        typeof value === "string" ? value.trim().toUpperCase() : "";
      if (str === "") return true;
      if (!isValidPanFormat(str)) return PAN_FORMAT_MESSAGE;
      return true;
    };
  }

  return rules;
}

export function resolveSelectOptions(
  field: ProcessedFormField,
): { name: string; display_name: string }[] {
  const source = field.list || field.options;
  if (source && Array.isArray(source)) {
    return source.map((item) => {
      if (typeof item === "string") {
        return { name: item, display_name: item };
      }
      return { name: item.name, display_name: item.display_name || item.name };
    });
  }
  if (field.fields && Array.isArray(field.fields)) {
    return field.fields.map((f) => {
      if (typeof f === "string") return { name: f, display_name: f };
      return { name: f.name, display_name: f.display_name || f.name };
    });
  }
  return [];
}

export function buildConditionalFieldNames(
  fields: ProcessedFormField[],
): Set<string> {
  return new Set(
    fields.filter((f) => isJexlString(f.hidden)).map((f) => f.name),
  );
}


export function stripConditionallyHiddenFields(
  data: Record<string, unknown>,
  visibleFieldNames: Set<string>,
  conditionalFieldNames: Set<string>,
  computedFieldNames: Set<string>,
): Record<string, unknown> {
  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    if (
      conditionalFieldNames.has(key) &&
      !visibleFieldNames.has(key) &&
      !computedFieldNames.has(key)
    )
      continue;
    filtered[key] = value;
  }
  return filtered;
}
